import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

import { initialState } from './index';

import { SURVEY_DEFAULT_OPTIONS } from '@/config';
import { nestedAssign } from '@/utils/ObjectUtils';

const actions = {
  'MISSION/RESET': (state, action) => initialState,
  'MISSION/SET_SPECS': (state, action) =>
    produce(state, (draft) => {
      draft.json.specs = action.payload.specs;
    }),
  'MISSION/CHANGE_NAME': (state, action) =>
    produce(state, (draft) => {
      draft.name = action.payload.name;
    }),
  'MISSION/LOAD': (state, action) =>
    produce(state, (draft) => {
      const { id, name, json } = action.payload;

      draft.id = id || uuidv4();
      draft.name = name;
      draft.json.specs = json.specs;
      draft.json.commands = json.commands;
      draft.json.fences = json.fences || [];
    }),
  'MISSION/SET_COLLISIONS': (state, action) =>
    produce(state, (draft) => {
      draft.collisions = action.payload.collisions;
    }),
  'MISSION/SET_ELEVATIONS': (state, action) =>
    produce(state, (draft) => {
      draft.elevations = action.payload.elevations;
    }),
  'MISSION/RESET_COMMANDS': (state, action) =>
    produce(state, (draft) => {
      draft.json.commands = [];
    }),
  'MISSION/REMOVE_COMMAND': (state, action) =>
    produce(state, (draft) => {
      const { index } = action.payload;

      const commands = [...state.json.commands];
      commands.splice(index, 1);
      draft.json.commands = commands;
    }),
  'MISSION/SHIFT_COMMAND': (state, action) =>
    produce(state, (draft) => {
      const { index, offset } = action.payload;

      const commands = [...state.json.commands];
      [commands[index], commands[index + offset]] = [commands[index + offset], commands[index]];
      draft.json.commands = commands;
    }),
  'MISSION/UPDATE_COMMAND': (state, action) =>
    produce(state, (draft) => {
      const { index, name, value } = action.payload;

      const commands = structuredClone(state.json.commands);
      // 값이 undefined 인 경우 필드 제거
      if (value === undefined) {
        delete commands[index].data[name];
      } else {
        commands[index].data[name] = value;
      }
      draft.json.commands = commands;
    }),
  'MISSION/APPEND_WAYPOINT': (state, action) =>
    produce(state, (draft) => {
      const { waypoint } = action.payload;

      const newCommand = {
        id: uuidv4(),
        type: state.json.commands.length === 0 ? 'navTakeoff' : 'navWaypoint',
        data: {
          name: '',
          ...waypoint,
        },
      };
      draft.json.commands = [...state.json.commands, newCommand];
    }),
  'MISSION/INSERT_WAYPOINT': (state, action) =>
    produce(state, (draft) => {
      const { index, waypoint } = action.payload;

      const newCommand = {
        id: uuidv4(),
        type: 'navWaypoint',
        data: {
          name: '',
          ...waypoint,
        },
      };
      const commands = structuredClone(state.json.commands);
      commands.splice(index + 1, 0, newCommand);
      draft.json.commands = commands;
    }),
  'MISSION/MOVE_WAYPOINT': (state, action) =>
    produce(state, (draft) => {
      const { id, waypoint } = action.payload;

      const index = state.json.commands.findIndex((command) => command.id === id);
      if (index === -1) return;

      const commands = structuredClone(state.json.commands); // to avoid read-only property
      commands[index].data.position = waypoint.position;
      commands[index].data.elevation = waypoint.elevation;
      draft.json.commands = commands;
    }),
  'MISSION/EDIT_WAYPOINT': (state, action) =>
    produce(state, (draft) => {
      const { index, name, value } = action.payload;

      const commands = structuredClone(state.json.commands);
      nestedAssign(commands[index].data, name.split('.'), value);
      draft.json.commands = commands;
    }),
  'MISSION/EDIT_ALL_WAYPOINTS': (state, action) =>
    produce(state, (draft) => {
      if (state.json.commands.length === 0) return;

      const { name, value } = action.payload;

      // Relative Altitude
      if (name === 'relAltitude') {
        draft.json.commands = state.json.commands.map((command) => {
          if (
            ['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navTakeoff', 'navWaypoint', 'cusSurvey'].includes(
              command.type,
            )
          ) {
            const newCommand = structuredClone(command);
            newCommand.data.altitude = value;
            return newCommand;
          }
          return command;
        });
      }
      // AGL Altitude
      else if (name === 'aglAltitude') {
        const homeElevation = state.json.commands[0].data.elevation;
        draft.json.commands = state.json.commands.map((command) => {
          if (['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navWaypoint'].includes(command.type)) {
            const newCommand = structuredClone(command);
            newCommand.data.altitude = value - homeElevation + command.data.elevation;
            return newCommand;
          }
          return command;
        });
      }
      // MSL Altitude
      else if (name === 'mslAltitude') {
        const homeElevation = state.json.commands[0].data.elevation;
        draft.json.commands = state.json.commands.map((command) => {
          if (['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navWaypoint'].includes(command.type)) {
            const newCommand = structuredClone(command);
            newCommand.data.altitude = value - homeElevation;
            return newCommand;
          }
          return command;
        });
      }
    }),
  'MISSION/APPEND_SURVEY': (state, action) =>
    produce(state, (draft) => {
      const { boundary, positions, altitude } = action.payload;

      const newCommand = {
        id: uuidv4(),
        type: 'cusSurvey',
        data: {
          ...SURVEY_DEFAULT_OPTIONS,
          boundary,
          positions,
          altitude,
        },
      };
      draft.json.commands = [...state.json.commands, newCommand];
    }),
  'MISSION/EDIT_SURVEY': (state, action) =>
    produce(state, (draft) => {
      const { index, values } = action.payload;

      const commands = structuredClone(state.json.commands);
      Object.entries(values).forEach(([name, value]) => {
        nestedAssign(commands[index].data, name.split('.'), value);
      });
      draft.json.commands = commands;
    }),
  'MISSION/APPEND_RETURN_TO_LAUNCH': (state, action) =>
    produce(state, (draft) => {
      const newCommand = { id: uuidv4(), type: 'navReturnToLaunch' };
      draft.json.commands = [...state.json.commands, newCommand];
    }),
};

export default actions;
